/**
 * Hilfsfunktionen rund um Bewertungsbögen — bewusst frei von React, damit
 * sie ohne Rendering testbar sind.
 */

import { IRatingForm, IRatingFormQuestion } from '../../Interfaces/IRatingForm';

/** Obergrenze für Sterne und Skalen. Beide werden dem Mandanten als einzelne
 *  Elemente gerendert (Sterne bzw. Knöpfe), mehr passt nicht in eine Zeile. */
export const MAX_SCALE_STEPS = 10;

/** Untergrenze der *Obergrenze*: mit nur einer Stufe gibt es genau einen
 *  wählbaren Wert — die Frage kann dann nichts ausdrücken und würde aus dem
 *  Gesamtwert fallen (`_scoreOnCommonScale` liefert bei rmax <= rmin None). */
export const MIN_SCALE_STEPS = 2;

/** Der Gesamtwert einer Bewertung liegt immer zwischen 1 und 5 — jede Antwort
 *  wird vor der Mittelung auf diese Skala umgerechnet (`OVERALL_SCALE_MIN` /
 *  `OVERALL_SCALE_MAX` in ratingform.py). Ein Schwellenwert ausserhalb dieser
 *  Spanne waere unerreichbar: der Bewertungs-Link erschiene dann nie. */
export const THRESHOLD_MIN = 1;
export const THRESHOLD_MAX = 5;

/**
 * Schwellenwert beim Verlassen des Feldes auf die gueltige Spanne bringen.
 *
 * Ein leeres Feld bleibt leer: das bedeutet „kein Schwellenwert“ und damit,
 * dass der Bewertungs-Link gar nicht angezeigt wird — ein gueltiger Zustand,
 * der nicht stillschweigend mit einer Zahl belegt werden darf.
 */
export const thresholdOnBlur = (
  value: number | null | undefined
): number | null => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return Math.min(Math.max(value, THRESHOLD_MIN), THRESHOLD_MAX);
};

const clampToRange = (n: number): number =>
  Math.min(Math.max(Math.trunc(n), MIN_SCALE_STEPS), MAX_SCALE_STEPS);

/**
 * Eingabe während des Tippens.
 *
 * Bewusst nur nach oben und gegen negative Werte begrenzt, NICHT auf das
 * Minimum: wer „10" tippen will, gibt zuerst „1" ein — würde das sofort auf
 * 2 hochgezogen, käme am Ende „20" heraus. Das Einrasten übernimmt
 * `scaleMaxOnBlur`.
 *
 * Rückgabe `null` = Feld geleert.
 */
export const scaleMaxWhileTyping = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return null;
  return Math.min(Math.max(Math.trunc(parsed), 0), MAX_SCALE_STEPS);
};

/**
 * Verlassen des Feldes: auf den gültigen Bereich einrasten. `fallback` greift,
 * wenn das Feld leer ist (typabhängig: 5 Sterne bzw. Skala bis 10).
 */
export const scaleMaxOnBlur = (
  value: number | null | undefined,
  fallback: number
): number => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return clampToRange(fallback);
  }
  return clampToRange(value);
};

/**
 * Wandelt einen Flag-Wert aus dem Backend in ein echtes Boolean.
 *
 * MariaDB liefert TINYINT(1) als Zahl `0`/`1`. Der frühere Ausdruck
 * `wert !== false` war deshalb falsch: `0 !== false` ist in JavaScript
 * `true`, weil der strikte Vergleich unterschiedliche Typen nie als gleich
 * ansieht. Ein gespeichertes `0` erschien dadurch im Editor als aktiviert
 * und wurde beim nächsten Speichern als `1` zurückgeschrieben — die
 * Einstellung kippte still um.
 *
 * `fallback` greift nur, wenn das Feld gar nicht geliefert wurde.
 */
export const flagFromBackend = (value: unknown, fallback: boolean): boolean => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return fallback;
    return trimmed !== '0' && trimmed.toLowerCase() !== 'false';
  }
  return Boolean(value);
};

export interface IQuestionSummary {
  /** Kurzform von Typ und Umfang, z. B. „★ 1–5" */
  typeChip: string;
  required: boolean;
  /** Nur relevant, wo „zählt zum Gesamtwert" überhaupt wählbar ist. */
  excludedFromOverall: boolean;
  labelMissing: boolean;
}

/** Was in der kompakten Zeile einer Frage steht. */
export const questionSummary = (q: IRatingFormQuestion): IQuestionSummary => {
  const capped = (fallback: number): number =>
    Math.min(q.RangeMax ?? fallback, MAX_SCALE_STEPS);

  let typeChip: string;
  switch (q.QuestionType) {
    case 'stars':
      typeChip = `★ 1–${capped(5)}`;
      break;
    case 'scale':
      typeChip = `Skala 1–${capped(10)}`;
      break;
    case 'yesno':
      typeChip = 'Ja/Nein';
      break;
    default:
      typeChip = 'Text';
  }

  // Bei Text und Ja/Nein ist "zaehlt nicht" der vom Typ vorgegebene
  // Normalfall — als Hinweis waere es nur Rauschen.
  const scorable = q.QuestionType === 'stars' || q.QuestionType === 'scale';

  return {
    typeChip,
    required: flagFromBackend(q.IsRequired, true),
    excludedFromOverall: scorable && !flagFromBackend(q.CountsForOverall, true),
    labelMissing: (q.Label ?? '').trim() === '',
  };
};

let clientKeyCounter = 0;

/** Stabile Identität einer Editor-Zeile. Lebt nur im Browser. */
export const nextClientKey = (): string => `q${++clientKeyCounter}`;

export const withClientKeys = (
  questions: IRatingFormQuestion[]
): IRatingFormQuestion[] =>
  questions.map(q =>
    q._clientKey ? q : { ...q, _clientKey: nextClientKey() }
  );

/** Vor dem Speichern: der Schlüssel gehört nicht ins Backend. */
export const stripClientKeys = (
  questions: IRatingFormQuestion[]
): IRatingFormQuestion[] => questions.map(({ _clientKey, ...rest }) => rest);

export const mailSectionSummary = (form: IRatingForm): string =>
  flagFromBackend(form.ConfirmationEnabled, false)
    ? 'Eingangsbestätigung an'
    : 'Eingangsbestätigung aus';

/**
 * Der fehlende Link hat Vorrang vor dem Schwellenwert: das ist der Zustand,
 * in dem der Bewertungs-Anstoss wirkungslos bleibt, ohne dass es auffällt.
 */
export const thanksSectionSummary = (form: IRatingForm): string => {
  if ((form.ThanksLinkUrl ?? '').trim() === '') return 'kein Link hinterlegt';
  if (form.ThresholdScore === null || form.ThresholdScore === undefined) {
    return 'Link ohne Schwellenwert';
  }
  return `ab ${form.ThresholdScore} ★`;
};
